import {useLeafletContext} from "@react-leaflet/core";
import L from 'leaflet';
import {memo, useCallback, useEffect, useMemo, useRef, useState} from "react";
import GeoTiffLayer from "./GeoTiffLayer";
import PMTilesLayer from "./PMTilesLayer";

const FloorControl = ({floors = [], setLoaded, handleSelectFloor, currentFloor = null, disableSelection = false, hideSelector = false}) => {
    const context = useLeafletContext();
    const buttonRefs = useRef([]);
    const [rasterLoaded, setRasterLoaded] = useState({});
    const controlAdded = useRef(false);

    const floorSelectionHandler = useCallback((floor) => {
        if (disableSelection) {
            return;
        }
        handleSelectFloor(floor.value);
    }, [disableSelection, handleSelectFloor]);
    const allFloorsLoaded = useMemo(() => {
        const floorsLoaded = floors.map((f) => rasterLoaded[f.name]).filter(Boolean);
        return floors.length > 0 && floorsLoaded.length === floors.length;
    }, [floors, rasterLoaded]);
    const selectedFloor = useMemo(() => {
        if (!allFloorsLoaded || !floors.length) {
            return "";
        }

        return floors.find((floor) => floor.value === currentFloor?.value)?.name ?? floors[0].name;
    }, [allFloorsLoaded, currentFloor, floors]);

    useEffect(() => {
        setLoaded(allFloorsLoaded);
    }, [allFloorsLoaded, setLoaded]);

    useEffect(() => {
        // Unified View supplies context-aware floor buttons while these layers still load.
        if (hideSelector) return;
        const FloorSelectorControl = L.Control.extend({
            onAdd: function (map) {
                const floorSelector = L.DomUtil.create('div', "floor-plan-selector-container");
                floors.forEach((floor, idx) => {
                    const floorButtonElement = L.DomUtil.create("a", "floor-plan-button", floorSelector);
                    floorButtonElement.innerText = floor.name;
                    L.DomEvent.disableClickPropagation(floorButtonElement);
                    L.DomEvent.on(floorButtonElement, "click", () => floorSelectionHandler(floor));
                    buttonRefs.current.push({floor, floorButtonElement});
                })
                floorSelector.title = "Select Floor";
                return floorSelector;
            },

            onRemove: function (map) {
                // Nothing to do here
            }
        });

        const floorSelectorControl = new FloorSelectorControl({position: "topleft"});
        if (!controlAdded.current) {
            context.map.addControl(floorSelectorControl);
            controlAdded.current = true;
        }

        return () => {
            context.map.removeControl(floorSelectorControl);
            controlAdded.current = false;
            buttonRefs.current = [];
        };
    }, [floors, context.map, floorSelectionHandler, hideSelector]);

    // change button selected state
    useEffect(() => {
        if (selectedFloor && buttonRefs.current) {
            // remove selected class from all buttons
            buttonRefs.current.forEach(f => L.DomUtil.removeClass(f.floorButtonElement, "floor-plan-button-selected"));
            // add selected class
            const selectedElement = buttonRefs.current.find(f => f.floor.name === selectedFloor);
            if (selectedElement) {
                L.DomUtil.addClass(selectedElement.floorButtonElement, "floor-plan-button-selected");
            }
        }
    }, [disableSelection, selectedFloor]);

    useEffect(() => {
        buttonRefs.current.forEach(({floorButtonElement}) => {
            floorButtonElement.style.pointerEvents = disableSelection ? "none" : "auto";
        });
    }, [disableSelection]);

    let loaded = (floor) => (loaded) => setRasterLoaded(prev => ({...prev, [floor]: loaded}));
    return <>
        {
            floors.map(floor => {
                const urlFileExtension = getFileExtension(floor.url);
                const isGeoTiff = urlFileExtension === 'tiff' || urlFileExtension === 'tif';
                const isPMTiles = urlFileExtension === 'pmtiles';
                return isPMTiles ? <PMTilesLayer key={floor.name}
                                                 url={floor.url}
                                                 id={floor.name}
                                                 setLoaded={loaded(floor.name)}
                                                 isVisible={selectedFloor === floor.name}/>
                    : isGeoTiff ? <GeoTiffLayer key={floor.name}
                                                url={floor.url}
                                                options={{resolution: 256, opacity: 1, zoom: 30}}
                                                setLoaded={loaded(floor.name)}
                                                isVisible={selectedFloor === floor.name}/>
                        : null
            })
        }</>;
}

export default memo(FloorControl);

function getFileExtension(url) {
    if (typeof url !== 'string' || url.trim() === '') {
        console.error('Input must be a non-empty string.');
        return '';
    }

    try {
        // console.log("url", url)
        const lastDotIndex = url.lastIndexOf('.');
        const lastSlashIndex = url.lastIndexOf('/');

        // Check if a dot exists and if it comes after the last slash.
        if (lastDotIndex > lastSlashIndex) {
            const extension = url.substring(lastDotIndex + 1);
            return extension.toLowerCase();
        }
    } catch (e) {
        console.error('Invalid URL provided:', e.message);
    }

    return '';
}
